import PopupBasicLazy from '../components/popups/PopupBasicLazy';
import i18n from 'src/lib/i18n/i18n';
import uiConfig from 'src/lib/ui/config';
import ButtonScaleViewWithText from 'src/lib/ui/components/ButtonScaleViewWithText';
import bitmapFonts from 'src/lib/bitmapFonts';
import View from '@play-co/timestep-core/lib/ui/View';
import sounds from 'src/lib/sounds';
import Scroll from '../components/shared/Scroll';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import { openPopupPromise } from 'src/lib/popups/popupOpenClose';
import StateObserver, { user, now } from 'src/StateObserver';
import { GCInstant } from 'src/lib/gcinstant';
import { GiftType } from 'src/replicant/gifts';
import * as Gifts from 'src/replicant/gifts';
import { createEmitter } from 'src/lib/Emitter';
import { animDuration } from 'src/lib/utils';
import { sendGiftOA, sendMassGiftsShare } from './social';
import { captureGenericError } from 'src/lib/sentry';
import {
  getCollectableGiftSenders,
  getTimeUntilGiftCapReset,
  sortFriendsForGifting,
} from './helpers';
import {
  GiftBatchResult,
  isResolvingGifts,
  resolveGiftsInBatches,
} from './gifts.batch';
import { Feature } from 'src/replicant/analytics';
import {
  GiftOrigin,
  GiftPopupCloseReason,
  trackGiftCollect,
  trackGiftPopupClose,
  trackGiftPopupOpen,
  trackGiftSendAll,
  trackGiftSendError,
} from './analytics';
import { animateCoins, animateEnergy } from '../animation/CurrencyAnimation';
import { trackPinchConversion } from 'src/lib/analytics/events';
import { getMutex } from 'src/replicant/utils/mutex';
import { GiftsFriendRow, GiftsListItem } from './components/GiftsFriendRow';
import { GiftsCollectedCounter } from './components/GiftsCollectedCounter';
import { Tooltip } from '../components/tooltip/Tooltip';
import { formatDurationShort } from 'src/lib/formatDuration';

type CloseHandler = (closeReason: GiftPopupCloseReason) => void;

type FriendsListItem = GiftsListItem;

function getAnalyticsResource(giftType: GiftType) {
  switch (giftType) {
    case 'bPoint':
      return 'bPoints';
    case 'energy':
      return 'energy';
    case 'gems':
      return 'gems';
    default:
      return giftType;
  }
}

/**
 * Gifts shop popup
 */
export default class PopupGiftsList extends PopupBasicLazy {
  private scroll: Scroll<FriendsListItem, GiftsFriendRow>;
  private close: CloseHandler;
  private collectAndSendAllButton: ButtonScaleViewWithText;
  private collectedCounter: GiftsCollectedCounter;
  private collectPreview: LangBitmapFontTextView;
  private infoTooltip: Tooltip;
  private infoTooltipText: LangBitmapFontTextView;
  private interactionMutex = getMutex();

  private originFeature!: GiftOrigin;
  private collectableSendersCount = 0;
  private sendableReceiversCount = 0;
  private giftsSentCount = 0;
  private giftsCollectedCount = 0;

  /** Refreshes the cap state when the daily gift gem cap resets */
  private capResetTimer?: ReturnType<typeof setTimeout>;

  /** True while a batched collect-and-send run is in flight. */
  private isBatchRunning = false;

  /**
   * Popup constructor
   */
  constructor(opts: { superview: View; close: CloseHandler }) {
    super({
      superview: opts.superview,
      close: () => this.close('close_x'),
      zIndex: 10000,
      width: 582,
      height: 820,
      darkerBg: true,
      titleIcon: { image: 'assets/ui/gifts/icons/gifts_popup_icon.png' },
      tracking: { feature: 'gifts', $subFeature: 'gifts-list-popup' },
    });

    this.close = (closeReason) => {
      trackGiftPopupClose({
        closeReason,
        giftsSentCount: this.giftsSentCount,
        giftsCollectedCount: this.giftsCollectedCount,
      });
      opts.close(closeReason);
    };

    this.message.updateOpts({
      size: 20,
      x: 54,
      y: 91,
      width: 310,
      height: 54,
      leading: 0,
      align: 'left',
    });

    this.collectedCounter = new GiftsCollectedCounter({
      superview: this.box,
      x: 375,
      y: 84,
    });

    // Mirrors the close button on the left side
    const infoButtonOpts = uiConfig.buttons.info;
    const infoButtonX = infoButtonOpts.width * 0.5 - 10;
    const infoButtonY = 11;

    new ButtonScaleViewWithText({
      ...infoButtonOpts,
      superview: this.box,
      x: infoButtonX,
      y: infoButtonY,
      onClick: async () => this.toggleInfoTooltip(),
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-list-popup-info',
      },
    });

    this.infoTooltip = new Tooltip({
      superview: this.box,
      x: infoButtonX,
      y: infoButtonY + infoButtonOpts.height * 0.5,
      width: 400,
      direction: 'down',
      tipAnchorX: 0,
      zIndex: 2000,
    });

    this.infoTooltipText = new LangBitmapFontTextView({
      superview: this.infoTooltip.content,
      width: 340,
      height: 70,
      align: 'center',
      verticalAlign: 'center',
      size: 22,
      color: '#9A7171',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => this.getInfoTooltipText(),
    });

    const scrollWidth = 535;

    // Recycled: only the visible rows (and their avatars) exist at any time
    this.scroll = new Scroll<FriendsListItem, GiftsFriendRow>({
      superview: this.box,
      createItem: (superview) =>
        new GiftsFriendRow({
          superview,
          createActionButton: (item) => this.createActionButton(item),
          getActivityText: (item) => this.getActivityText(item),
          onInvite: () => this.onInvite(),
        }),
      recycle: true,
      setData: (_, item, row) => row.setData(item),
      showBg: true,
      firstItemOffset: 5,
      margin: 7,
      rect: {
        x: (this.box.style.width - scrollWidth) * 0.5,
        y: 177,
        width: scrollWidth,
        height: 475,
      },
    });

    this.collectPreview = new LangBitmapFontTextView({
      superview: this.box,
      x: 40,
      y: 656,
      width: this.box.style.width - 80,
      height: 30,
      align: 'center',
      verticalAlign: 'center',
      size: 20,
      color: '#BF8147',
      wordWrap: false,
      font: bitmapFonts('Body'),
      localeText: () => '',
    });

    this.collectAndSendAllButton = new ButtonScaleViewWithText({
      ...uiConfig.buttons.secondary,
      superview: this.box,
      labelOffsetY: 0,
      labelOffsetX: -9,
      fontSize: 30,
      font: bitmapFonts('Body'),
      x: this.box.style.width / 2,
      y: 742,
      width: 360,
      height: 94.7,
      centerOnOrigin: true,
      localeText: () => this.getCollectAndSendAllText(),
      onClick: async () => {
        if (this.interactionMutex.isLocked()) return;
        await this.interactionMutex.lock();

        try {
          trackGiftSendAll({
            origin: this.originFeature,
            collectableSendersCount: this.collectableSendersCount,
            sendableReceiversCount: this.sendableReceiversCount,
          });

          await this.collectAndSendAll(this.collectAndSendAllButton);
        } finally {
          this.interactionMutex.release();
        }
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-list-popup-collect-and-send-all',
      },
    });

    // Update list on state changes
    createEmitter(this.box, ({ user }) => ({
      received: user.gifts.received,
      sent: user.gifts.sent,
      collected: user.gifts.collected,
    })).addListener(() => {
      // A batched run writes gifts.sent once per batch. Refreshing per batch
      // rebuilds the whole friend list and re-enables the send button mid-run, so
      // defer to the single refresh at the end of the run.
      if (!this.isBatchRunning) this.refresh();
    });
  }

  public getAssetsGroup() {
    return null;
  }

  private getGiftType(): GiftType {
    return 'gems';
  }

  private getAnalyticsContext() {
    const state = StateObserver.getState().user;
    const timestamp = StateObserver.now();

    // Same senders the bulk action collects, so capped gifts do not count
    const collectable = new Set(
      getCollectableGiftSenders(state, timestamp, this.getFriendIds()),
    );

    return {
      collectableSendersCount: GCInstant.friends.filter((friend) =>
        collectable.has(friend.id),
      ).length,
      sendableReceiversCount: GCInstant.friends.filter((friend) =>
        Gifts.getters.canSendGift(state, friend.id, timestamp),
      ).length,
    };
  }

  /**
   * Invite row button handler
   */
  private async onInvite() {
    if (this.interactionMutex.isLocked()) return;
    await this.interactionMutex.lock();

    try {
      sounds.playSound('ok');
      this.close('close_invite');
    } finally {
      this.interactionMutex.release();
    }
  }

  /**
   * Get collect and send button style
   */
  private makeCollectAndSendButton(friend: FriendsListItem) {
    const button = new ButtonScaleViewWithText({
      tag: 'collect-and-send-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: 0,
      labelOffsetY: -2,
      x: 510 - 150,
      y: 12,
      width: 142,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.collectAndSentGiftBtn'),
      onClick: async () => {
        if (this.interactionMutex.isLocked()) return;
        await this.interactionMutex.lock();

        try {
          sounds.playSound('ok');
          await this.collectAndSendGift(button, friend);
        } finally {
          this.interactionMutex.release();
        }
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-collect-and-send-button',
      },
    });

    return button;
  }

  /**
   * Get collect button style
   */
  private makeCollectButton(friend: FriendsListItem) {
    const button = new ButtonScaleViewWithText({
      tag: 'collect-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 510 - 150,
      y: 12,
      width: 142,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.collectGiftBtn'),
      onClick: async () => {
        if (this.interactionMutex.isLocked()) return;
        await this.interactionMutex.lock();

        try {
          sounds.playSound('ok');
          await this.collectGift(button, friend);
        } finally {
          this.interactionMutex.release();
        }
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-collect-button',
      },
    });

    return button;
  }

  /**
   * Get send button style
   */
  private makeSendButton(friend: FriendsListItem) {
    const button = new ButtonScaleViewWithText({
      tag: 'send-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 510 - 130,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.sendGiftBtn'),
      onClick: async () => {
        if (this.interactionMutex.isLocked()) return;
        await this.interactionMutex.lock();

        try {
          sounds.playSound('ok');
          const subFeatureMap: Record<GiftType, string> = {
            energy: Feature.GIFTS.SEND_ENERGY,
            bPoint: Feature.GIFTS.SEND_BPOINTS,
            gems: Feature.GIFTS.SEND_GEMS,
          };
          const subFeature = subFeatureMap[this.getGiftType()];
          await this.sendGift(button, friend, subFeature);
        } finally {
          this.interactionMutex.release();
        }
      },
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-send-button',
      },
    });

    return button;
  }

  /**
   * Already sent disabled button
   */
  private makeSentButton() {
    const button = new ButtonScaleViewWithText({
      tag: 'send-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 510 - 130,
      y: 12,
      width: 124,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.sentGiftBtn'),
      onClick: async () => {},
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-sent-button',
      },
    });

    button.setDisabled(true);

    return button;
  }

  /**
   * Disabled button for a gift held back by the daily cap
   */
  private makeDailyLimitButton() {
    const button = new ButtonScaleViewWithText({
      tag: 'daily-limit-button',
      ...uiConfig.buttons.secondary,
      font: bitmapFonts('Body'),
      fontSize: 23,
      labelOffsetX: -3,
      labelOffsetY: -2,
      x: 510 - 150,
      y: 12,
      width: 142,
      height: 66,
      lineSpacing: -14,
      localeText: () => i18n('gifts.popupGiftsList.dailyLimitBtn'),
      onClick: async () => {},
      tracking: {
        feature: 'gifts',
        $subFeature: 'gifts-daily-limit-button',
      },
    });

    button.setDisabled(true);

    return button;
  }

  // -----------------------------------------------------------------
  // Business logic below
  // -----------------------------------------------------------------

  /**
   * On dialog init
   */
  public async init({
    originFeature,
  }: {
    // Origin of the popup
    originFeature: GiftOrigin;
    // Used just to force typing, additionalTracking in some reason won't be not sent to init opts.
    additionalTracking: {
      originFeature: GiftOrigin;
    };
  }) {
    const messageKeyMap: Record<GiftType, string> = {
      energy: 'gifts.popupGiftsList.messageEnergy',
      bPoint: 'gifts.popupGiftsList.message',
      gems: 'gifts.popupGiftsList.messageGems',
    };
    const messageKey = messageKeyMap[this.getGiftType()];

    super.init({
      title: i18n('gifts.popupGiftsList.title'),
      message: i18n(messageKey),
    });

    this.originFeature = originFeature;

    const analyticsContext = this.getAnalyticsContext();
    this.collectableSendersCount = analyticsContext.collectableSendersCount;
    this.sendableReceiversCount = analyticsContext.sendableReceiversCount;

    trackGiftPopupOpen({
      origin: this.originFeature,
      ...analyticsContext,
    });

    // Update list UI
    this.refresh();

    // Nothing in gift state changes at 00:00 JST, so refresh the cap state
    // once at the reset. Firing after close is harmless, see collectAndSendAll.
    clearTimeout(this.capResetTimer);
    this.capResetTimer = setTimeout(
      () => this.refresh(),
      getTimeUntilGiftCapReset(now()),
    );
  }

  /**
   * Refresh list UI
   */
  private refresh() {
    // Set state of collect all & send all button
    this.collectAndSendAllButton.setDisabled(!this.canCollectAll());
    this.collectAndSendAllButton.localeText = () =>
      this.getCollectAndSendAllText();

    // Daily cap counter and the gems the bulk action would collect
    this.collectedCounter.refresh();
    const { received } = user().gifts;
    const previewGems = this.getVisibleCollectableSenders().reduce(
      (sum, id) => sum + (received[id].gems ?? 0),
      0,
    );
    this.collectPreview.localeText = () =>
      previewGems
        ? i18n('gifts.popupGiftsList.collectPreview', {
            amount: previewGems,
            max: Gifts.ruleset.maxDailyGiftGems,
          })
        : '';
    this.infoTooltipText.localeText = () => this.getInfoTooltipText();

    // Get list of friends
    const list = this.getFriendsList();

    // Add invite item to the and of scroll
    list.push({ type: 'invite' });

    // Set friends list to scroll
    this.scroll.setItems(list);
  }

  /**
   * Actions button depend on state of target
   */
  private createActionButton(friend: FriendsListItem) {
    // Gift held back by the daily cap: send only, or nothing to do
    const isCapped = friend.hasGift && !friend.canCollect && this.isAtCap();

    // If we can collect and send
    if (friend.hasGift && friend.canSend) {
      return isCapped
        ? this.makeSendButton(friend)
        : this.makeCollectAndSendButton(friend);
    }

    // If we can collect
    if (friend.hasGift) {
      return isCapped
        ? this.makeDailyLimitButton()
        : this.makeCollectButton(friend);
    }

    // If we can send
    if (friend.canSend) {
      return this.makeSendButton(friend);
    }

    // Disable send button if we can't send and can't collect
    return this.makeSentButton();
  }

  /**
   * Get activity text
   */
  private getActivityText(friend: FriendsListItem) {
    const giftType = this.getGiftType();

    const giftReceivedMap: Record<GiftType, string> = {
      energy: i18n('gifts.popupGiftsList.activityText.sentYouEnergy'),
      bPoint: i18n('gifts.popupGiftsList.activityText.sentYouBpoints'),
      gems: i18n('gifts.popupGiftsList.activityText.sentYouGems'),
    };

    const giftSendMap: Record<GiftType, string> = {
      energy: i18n('gifts.popupGiftsList.activityText.letsSendEnergy'),
      bPoint: i18n('gifts.popupGiftsList.activityText.letsSendBpoints'),
      gems: i18n('gifts.popupGiftsList.activityText.letsSendGems'),
    };

    // If we can collect and send
    if (friend.hasGift && friend.canSend) {
      return giftReceivedMap[giftType];
    }

    // If we can collect
    if (friend.hasGift) {
      return giftReceivedMap[giftType];
    }

    // If we can send
    if (friend.canSend) {
      return giftSendMap[giftType];
    }

    // Default
    return '';
  }

  /**
   * canSendGift helper
   */
  private canSend(friendId: string) {
    const now = StateObserver.now();
    const state = StateObserver.getState().user;
    return Gifts.getters.canSendGift(state, friendId, now);
  }

  /**
   * hasGift helper
   */
  private hasGift(friendId: string) {
    const state = StateObserver.getState().user;
    return Gifts.getters.hasGift(state, friendId);
  }

  /**
   * canCollect helper
   */
  private getGiftRewardsTotal() {
    const now = StateObserver.now();
    const state = StateObserver.getState().user;
    return Gifts.getters.getAvailableGiftRewardsTotal(state, now);
  }

  private getFriendIds() {
    return GCInstant.friends.map((friend) => friend.id);
  }

  /**
   * Gifts the bulk action collects. Only friends shown in the list count, so
   * the preview never promises a gift from a sender without a row.
   */
  private getVisibleCollectableSenders() {
    return getCollectableGiftSenders(user(), now(), this.getFriendIds());
  }

  /**
   * No more gem gifts can be collected today
   */
  private isAtCap() {
    return !Gifts.getters.canCollectGiftGems(user(), now());
  }

  /**
   * Bottom button label follows what the bulk action will do
   */
  private getCollectAndSendAllText() {
    if (isResolvingGifts()) {
      return i18n('gifts.popupGiftsList.collectAndSendAllBtn');
    }

    const friends = this.getFriendsList();
    const someCanCollect = friends.some((friend) => friend.canCollect);
    const someCanSend = friends.some((friend) => friend.canSend);

    if (someCanCollect && !someCanSend) {
      return i18n('gifts.popupGiftsList.collectAllBtn');
    }

    if (!someCanCollect && someCanSend) {
      return i18n('gifts.popupGiftsList.sendAllBtn');
    }

    return i18n('gifts.popupGiftsList.collectAndSendAllBtn');
  }

  /**
   * Info tooltip explains the allowance, or the reset time at the cap
   */
  private getInfoTooltipText() {
    if (!this.isAtCap()) {
      return [
        i18n('gifts.popupGiftsList.allowance'),
        i18n('gifts.popupGiftsList.reset'),
      ].join('\n');
    }

    return [
      i18n('gifts.popupGiftsList.limitReached'),
      i18n('gifts.popupGiftsList.resetsIn', {
        time: formatDurationShort(getTimeUntilGiftCapReset(now())),
      }),
    ].join('\n');
  }

  private async toggleInfoTooltip() {
    if (this.infoTooltip.isShown) {
      await this.infoTooltip.hideTooltip();
      return;
    }

    this.infoTooltipText.localeText = () => this.getInfoTooltipText();
    await this.infoTooltip.showTooltip();
  }

  /**
   * Can collect all helper
   */
  private canCollectAll() {
    // A run started by an earlier popup instance may still be in flight; its
    // recipients are not stamped yet, so they still look sendable here.
    if (isResolvingGifts()) return false;

    const friends = this.getFriendsList();

    const someCanCollect = friends.some((friend) => friend.canCollect);
    const someCanSend = friends.some((friend) => friend.canSend);

    return someCanCollect || someCanSend;
  }

  /**
   * Return list of friends
   */
  private getFriendsList(): FriendsListItem[] {
    const now = StateObserver.now();
    const { user } = StateObserver.getState();

    const list: FriendsListItem[] = GCInstant.friends.map((friend) => {
      return {
        type: 'friend',
        id: friend.id,
        name: friend.name,
        // LINE serves a fixed 200x200 thumbnail; the original can be any size
        photo: friend.photoLarge ?? friend.photo,
        hasGift: Gifts.getters.hasGift(user, friend.id),
        canSend: Gifts.getters.canSendGift(user, friend.id, now),
        // The daily gem cap is enforced on the client only (#12235)
        canCollect:
          Gifts.getters.canCollect(user, friend.id, now) &&
          (!user.gifts.received[friend.id]?.gems ||
            Gifts.getters.canCollectGiftGems(user, now)),
      };
    });

    return sortFriendsForGifting(list) as FriendsListItem[];
  }

  /**
   * Send gift to user
   */
  private async sendGift(
    button: ButtonScaleViewWithText,
    friend: FriendsListItem,
    subFeature: string,
  ) {
    button.setDisabled(true);

    if (!friend.id) return;

    try {
      await this.sendGiftsReplicantMessage([friend.id]);
      this.giftsSentCount += 1;
    } catch (e) {
      trackGiftSendError({
        error: e,
        origin: this.originFeature,
        isMassSent: false,
        messagesCount: 1,
        gift: this.getGiftType(),
        step: 'single-replicant-message',
      });
      captureGenericError('Failed to send gift message', e);
      button.setDisabled(!this.canSend(friend.id));
      return;
    }

    // Send OA notification after the reward is recorded.
    try {
      await sendGiftOA(
        friend.id,
        this.getGiftType(),
        subFeature,
        this.originFeature,
      );
    } catch (e) {
      trackGiftSendError({
        error: e,
        origin: this.originFeature,
        isMassSent: false,
        messagesCount: 1,
        gift: this.getGiftType(),
        step: 'single-oa-message',
      });
      captureGenericError('Failed to send gift OA notification', e);
    }

    // Update button state
    button.setDisabled(!this.canSend(friend.id));
  }

  /**
   * Collect gift
   */
  private async collectGift(
    button: ButtonScaleViewWithText,
    friend: FriendsListItem,
  ) {
    button.setDisabled(true);

    // Over the daily gem cap: skip silently, the gift stays pending (#12235).
    // Checked live, the row can be stale after another collect.
    const pendingGift = friend.id
      ? StateObserver.getState().user.gifts.received[friend.id]
      : undefined;
    if (pendingGift?.gems && this.isAtCap()) return;

    // Friend has gift but you can't collect it
    if (!friend.canCollect) {
      await openPopupPromise('popupModalWhite', {
        title: i18n('gifts.popupCollectError.title'),
        message: i18n('gifts.popupCollectError.message', {
          max: Gifts.ruleset.maxClaimEnergy,
        }),
        tracking: {
          feature: 'gifts',
          $subFeature: 'gifts-cant-collect',
        },
      });

      return;
    }

    if (!friend.id) return;

    // Show gift collection animation
    const { user } = StateObserver.getState();
    const gift = user.gifts.received[friend.id];
    await this.animateGiftCollection(button, gift);

    // Give reward
    const analytics = await StateObserver.invoke.consumeGift(friend.id);
    // Send client side analytics
    if (analytics) {
      trackGiftCollect(analytics);
      this.giftsCollectedCount += 1;
    }

    trackPinchConversion({
      action: 'social',
      feature: Feature.GIFTS._,
      subFeature: `${Feature.GIFTS._}_${analytics.gift}_single`,
      resource: getAnalyticsResource(analytics.gift),
    });

    // Update button state
    button.setDisabled(!this.hasGift(friend.id));
  }

  /**
   * Collect and send gift
   */
  private async collectAndSendGift(
    button: ButtonScaleViewWithText,
    friend: FriendsListItem,
  ) {
    button.setDisabled(true);

    // Run collect logic
    await this.collectGift(button, friend);

    const subFeatureMap: Record<GiftType, string> = {
      energy: Feature.GIFTS.SEND_ENERGY_AND_COLLECT,
      bPoint: Feature.GIFTS.SEND_BPOINTS_AND_COLLECT,
      gems: Feature.GIFTS.SEND_GEMS_AND_COLLECT,
    };

    // Run send logic
    const subFeature = subFeatureMap[this.getGiftType()];
    await this.sendGift(button, friend, subFeature);

    // Update UI state
    this.refresh();
  }

  /**
   * Collect and send gifts to all available user
   */
  private async collectAndSendAll(button: ButtonScaleViewWithText) {
    // Find friends available for messages
    const friends = this.getFriendsList().filter((friend) => friend.canSend);

    button.setDisabled(true);

    // Show reward confirmation popup
    const availableRewards = this.getGiftRewardsTotal();

    // We may already collect all energy,
    // in this case it will just send messages and skip reward popup
    const hasRewards = !!(
      availableRewards.energy ||
      availableRewards.bPoints ||
      availableRewards.gems
    );

    if (hasRewards) {
      // Show energy collection animation
      await this.animateGiftCollection(button, availableRewards);
    }

    // Collect and send in bounded batches so one tap never fans out over the whole
    // friend list in a single replication request.
    this.isBatchRunning = true;

    let result: GiftBatchResult;
    try {
      result = await resolveGiftsInBatches({
        // Oldest first, trimmed to today's gem allowance (#12235)
        collectIds: hasRewards ? this.getVisibleCollectableSenders() : [],
        sendIds: friends.map((friend) => friend.id!),
        // Counted per batch so closing the popup mid-run reports most of what
        // landed. The close event fires synchronously, so the final batch or two
        // can still be confirmed after it.
        onBatchConfirmed: (batch) => {
          this.giftsCollectedCount += batch.collected.length;
          this.giftsSentCount += batch.sentIds.length;
        },
      });
    } catch (e) {
      this.isBatchRunning = false;
      throw e;
    }

    const { collected, sentIds, error } = result;

    if (error) {
      trackGiftSendError({
        error,
        origin: this.originFeature,
        isMassSent: true,
        messagesCount: friends.length,
        gift: this.getGiftType(),
        step: 'mass-replicant-message',
      });
      captureGenericError('Failed to send gift messages', error);
    }

    // Send analytics
    collected.forEach((data) => {
      trackGiftCollect(data);

      trackPinchConversion({
        action: 'social',
        feature: Feature.GIFTS._,
        subFeature: `${Feature.GIFTS._}_${data.gift}_single`,
        resource: getAnalyticsResource(data.gift),
      });
    });

    // Notify only the friends whose gift was actually recorded, so a partial run
    // never sends an OA message for a gift that was not written.
    //
    // Skipped after a replication failure: the client is out of sync, so the OA
    // action would only reject and report the same failure a second time.
    if (!error && sentIds.length) {
      try {
        await sendMassGiftsShare(
          sentIds,
          this.getGiftType(),
          this.originFeature,
        );
      } catch (e) {
        trackGiftSendError({
          error: e,
          origin: this.originFeature,
          isMassSent: true,
          messagesCount: sentIds.length,
          gift: this.getGiftType(),
          step: 'mass-oa-message',
        });
        captureGenericError('Failed to send gift OA notification', e);
      }
    }

    this.isBatchRunning = false;

    // Update UI state. A run outlives the popup, so this can land after it closed:
    // harmless, it rebuilds a list nobody is looking at and the views are dropped.
    this.refresh();
  }

  /**
   * Send reward messages and wait for them to replicate before OA notification.
   */
  private async sendGiftsReplicantMessage(friendsIds: string[]) {
    await StateObserver.invoke.sendGiftsMessages(friendsIds);
    await StateObserver.replicant.flush();
  }

  private async animateGiftCollection(
    source: View,
    giftRewards: Partial<Record<'bPoints' | 'energy', number>>,
  ) {
    const { x, y } = source.getPosition(this.root);
    const { width, height } = source.style;
    const position = {
      x: x + width / 2,
      y: y + height / 2,
    };
    const animateParams = {
      superview: this.root,
      start: { position, scale: 0.7 },
    };
    await Promise.all([
      animateCoins({ ...animateParams, count: giftRewards.bPoints ?? 0 }),
      animateEnergy({ ...animateParams, count: giftRewards.energy ?? 0 }),
    ]);
  }
}
